import type { ApiSchemas } from "@/shared/api/schema";
import { cn } from "@/shared/lib/css";

import { getNotificationColors } from "../../../shared/model/notification-colors";
import { getNotificationIcon } from "../../../shared/model/notification-icons";
import { formatNotificationTime } from "../domain/notification-utils";

type Notification = ApiSchemas["Notification"];

type NotificationCardProps = {
  notification: Notification;
  onClick: () => void;
  isPending?: boolean;
};

export function NotificationCard({
  notification,
  onClick,
  isPending,
}: NotificationCardProps) {
  const Icon = getNotificationIcon(notification.type);
  const colors = getNotificationColors(notification.type, notification.unread);
  const timeAgo = formatNotificationTime(notification.created_at);

  return (
    <button
      onClick={onClick}
      disabled={isPending}
      className={cn(
        "w-full text-left p-2 rounded-md transition-colors cursor-pointer",
        "hover:bg-muted/50",
        "disabled:opacity-50 disabled:cursor-not-allowed",
      )}
    >
      <div className="flex gap-3 items-start">
        {/* Icon container */}
        <div
          className={cn(
            "shrink-0 size-9 rounded-full flex items-center justify-center",
            colors.bg,
          )}
        >
          <Icon className={cn("size-4", colors.icon)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <h4
              className={cn(
                "text-sm font-medium truncate",
                notification.unread && "font-semibold",
              )}
            >
              {notification.title}
            </h4>
            {notification.unread && (
              <span className="shrink-0 size-2 rounded-full bg-primary" />
            )}
          </div>

          <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-line break-words">
            {notification.message}
          </p>

          <span className="text-xs text-muted-foreground/80">{timeAgo}</span>
        </div>
      </div>
    </button>
  );
}
